import { Building2, Plus, Trash2 } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import AuthenticatedLayout from '../components/AuthenticatedLayout'
import { companyService } from '../utils/companyService'

export default function CompaniesPage() {
  const navigate = useNavigate()
  const [companies, setCompanies] = useState<any[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
  })
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const [deleteConfirm, setDeleteConfirm] = useState<{ id: string; name: string } | null>(null)

  useEffect(() => {
    fetchCompanies()
  }, [])

  const fetchCompanies = async (page: number = 1) => {
    try {
      setIsLoading(true)
      setError(null)
      const response = await companyService.getAllCompanies(page, 10)

      if (response.statusCode === 200 && response.response) {
        const data: any = response.response
        
        // Handle different response formats
        let companiesArray: any[] = []
        let paginationData: any = {}
        
        if (Array.isArray(data)) {
          companiesArray = data
        } else if (typeof data === 'object') {
          companiesArray = data.companies || data.data || data.items || data.docs || []
          paginationData = data.pagination || data
        }
        
        setCompanies(companiesArray)
        setPagination({
          currentPage: paginationData.page || 1,
          totalPages: paginationData.totalPages || 1,
          totalItems: paginationData.total || companiesArray.length,
        })
      }
    } catch (err: any) {
      console.error('Error fetching companies:', err)
      setError('Failed to load companies')
    } finally {
      setIsLoading(false)
    }
  }

  const handleDelete = async (companyId: string) => {
    try {
      setDeletingId(companyId)
      setError(null)
      const response = await companyService.deleteCompany(companyId)

      if (response.statusCode === 200) {
        // Refresh the companies list
        await fetchCompanies(pagination.currentPage)
        setDeleteConfirm(null)
      } else {
        setError('Failed to delete company. Please try again.')
        setDeleteConfirm(null)
      }
    } catch (err: any) {
      console.error('Error deleting company:', err)
      setError(err.message || 'Failed to delete company. Please try again.')
      setDeleteConfirm(null)
    } finally {
      setDeletingId(null)
    }
  }

  return (
    <AuthenticatedLayout
      activeTab='companies'
    >
      <div className='grid grid-cols-1 gap-6'>
        {/* Companies Header */}
        <div className='p-6 bg-white rounded-2xl shadow-sm border-0'>
          <div className='flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-6'>
            <div className='flex items-start gap-2'>
              <Building2 className='w-5 h-5 text-gray-700 mt-1' />
              <h3 className='text-lg font-bold text-gray-900'>Your Companies</h3>
            </div>
            <button
              onClick={() => navigate('/add-company')}
              className='bg-[#001B38] text-white px-4 py-2 rounded-lg hover:bg-[#002a50] transition-colors flex items-center gap-2 w-full sm:w-auto justify-center'
            >
              <Plus className='w-4 h-4' />
              Add Company
            </button>
          </div>

          {isLoading ? (
            <div className='flex items-center justify-center py-12'>
              <div className='text-center'>
                <div className='animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto'></div>
                <p className='mt-4 text-gray-600'>Loading companies...</p>
              </div>
            </div>
          ) : error ? (
            <div className='bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md text-sm mb-4'>
              {error}
            </div>
          ) : companies.length === 0 ? (
            <div className='flex flex-col items-center justify-center py-12 border-2 border-dashed border-gray-200 rounded-lg'>
              <Building2 className='w-16 h-16 text-gray-300 mb-4' />
              <p className='text-gray-500 text-sm mb-2'>No companies added yet</p>
              <p className='text-gray-400 text-xs mb-4'>Add your first company to start filing</p>
              <button
                onClick={() => navigate('/add-company')}
                className='bg-[#001B38] text-white px-4 py-2 rounded-lg hover:bg-[#002a50] transition-colors flex items-center gap-2'
              >
                <Plus className='w-4 h-4' />
                Add Company
              </button>
            </div>
          ) : (
            <div>
              <div className='mb-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2'>
                <p className='text-sm text-gray-600'>
                  {pagination.totalItems} compan{pagination.totalItems !== 1 ? 'ies' : 'y'} found
                </p>
                {pagination.totalPages > 1 && (
                  <div className='flex items-center gap-2'>
                    <button
                      onClick={() => fetchCompanies(pagination.currentPage - 1)}
                      disabled={pagination.currentPage === 1}
                      className='px-3 py-1 text-sm border rounded disabled:opacity-50 disabled:cursor-not-allowed'
                    >
                      Previous
                    </button>
                    <span className='text-sm text-gray-600'>
                      Page {pagination.currentPage} of {pagination.totalPages}
                    </span>
                    <button
                      onClick={() => fetchCompanies(pagination.currentPage + 1)}
                      disabled={pagination.currentPage === pagination.totalPages}
                      className='px-3 py-1 text-sm border rounded disabled:opacity-50 disabled:cursor-not-allowed'
                    >
                      Next
                    </button>
                  </div>
                )}
              </div>

              {/* Mobile: Card View */}
              <div className='md:hidden space-y-3'>
                {companies.map((company) => {
                  const getAddress = () => {
                    if (!company.registeredOffice) return 'N/A'
                    const addressParts = [
                      company.registeredOffice.line1 || company.registeredOffice.addressLine1,
                      company.registeredOffice.city || company.registeredOffice.locality,
                      company.registeredOffice.postcode || company.registeredOffice.postalCode,
                    ].filter(Boolean)
                    return addressParts.length > 0 ? addressParts.join(', ') : 'N/A'
                  }
                  const getStatus = () => {
                    if (!company.companyStatus) return 'N/A'
                    return company.companyStatus.toUpperCase()
                  }
                  const getStatusColor = () => {
                    const status = (company.companyStatus || '').toLowerCase()
                    if (status === 'active') return 'bg-green-100 text-green-800'
                    return 'bg-gray-100 text-gray-800'
                  }
                  const companyName = company.companyName || company.name || 'N/A'

                  return (
                    <div
                      key={company._id}
                      onClick={() => navigate(`/company/${company._id}`)}
                      className='bg-white rounded-xl border border-gray-100 p-4 shadow-sm cursor-pointer hover:bg-gray-50 transition-colors'
                    >
                      <div className='flex items-start justify-between mb-2'>
                        <div>
                          <p className='text-sm font-semibold text-gray-900'>{companyName}</p>
                          <p className='text-xs text-gray-500 mt-0.5'>#{company.companyNumber || 'N/A'}</p>
                        </div>
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor()}`}>
                          {getStatus()}
                        </span>
                      </div>
                      <p className='text-xs text-gray-500 mb-3'>{getAddress()}</p>
                      <div className='flex justify-end'>
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            setDeleteConfirm({ id: company._id, name: companyName })
                          }}
                          disabled={deletingId === company._id}
                          className='inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-red-700 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed'
                        >
                          {deletingId === company._id ? (
                            <>
                              <div className='animate-spin rounded-full h-3 w-3 border-b-2 border-red-700'></div>
                              Deleting...
                            </>
                          ) : (
                            <>
                              <Trash2 className='w-3 h-3' />
                              Delete
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  )
                })}
              </div>

              {/* Desktop: Table View */}
              <div className='hidden md:block overflow-x-auto'>
                <table className='min-w-[700px] w-full divide-y divide-gray-200'>
                  <thead className='bg-gray-50'>
                    <tr>
                      <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                        Company Name
                      </th>
                      <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                        Company Number
                      </th>
                      <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                        Address
                      </th>
                      <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                        Status
                      </th>
                      <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className='bg-white divide-y divide-gray-200'>
                    {companies.map((company) => {
                      // Format address - check both field name formats
                      const getAddress = () => {
                        if (!company.registeredOffice) return 'N/A'

                        const addressParts = [
                          company.registeredOffice.line1 || company.registeredOffice.addressLine1,
                          company.registeredOffice.line2 || company.registeredOffice.addressLine2,
                          company.registeredOffice.city || company.registeredOffice.locality,
                          company.registeredOffice.county || company.registeredOffice.region,
                          company.registeredOffice.postcode || company.registeredOffice.postalCode,
                        ].filter(Boolean)

                        return addressParts.length > 0 ? addressParts.join(', ') : 'N/A'
                      }

                      // Format status - uppercase
                      const getStatus = () => {
                        if (!company.companyStatus) return 'N/A'
                        return company.companyStatus.toUpperCase()
                      }

                      // Get status badge color
                      const getStatusColor = () => {
                        const status = (company.companyStatus || '').toLowerCase()
                        if (status === 'active') {
                          return 'bg-green-100 text-green-800'
                        } else if (status === 'dissolved') {
                          return 'bg-gray-100 text-gray-800'
                        }
                        return 'bg-gray-100 text-gray-800'
                      }

                      const companyName = company.companyName || company.name || 'N/A'

                      return (
                        <tr
                          key={company._id}
                          className='hover:bg-gray-50 transition-colors'
                        >
                          <td
                            className='px-6 py-4 whitespace-nowrap cursor-pointer'
                            onClick={() => navigate(`/company/${company._id}`)}
                          >
                            <div className='text-sm font-medium text-gray-900 text-start'>
                              {companyName}
                            </div>
                          </td>
                          <td
                            className='px-6 py-4 whitespace-nowrap cursor-pointer'
                            onClick={() => navigate(`/company/${company._id}`)}
                          >
                            <div className='text-sm text-gray-600 text-start'>
                              {company.companyNumber || 'N/A'}
                            </div>
                          </td>
                          <td
                            className='px-6 py-4 cursor-pointer'
                            onClick={() => navigate(`/company/${company._id}`)}
                          >
                            <div className='text-sm text-gray-600 text-start'>
                              {getAddress()}
                            </div>
                          </td>
                          <td
                            className='px-6 py-4 whitespace-nowrap text-start cursor-pointer'
                            onClick={() => navigate(`/company/${company._id}`)}
                          >
                            <span
                              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor()}`}
                            >
                              {getStatus()}
                            </span>
                          </td>
                          <td className='px-6 py-4 whitespace-nowrap text-start'>
                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                setDeleteConfirm({ id: company._id, name: companyName })
                              }}
                              disabled={deletingId === company._id}
                              className='inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-red-700 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed'
                            >
                              {deletingId === company._id ? (
                                <>
                                  <div className='animate-spin rounded-full h-3 w-3 border-b-2 border-red-700'></div>
                                  Deleting...
                                </>
                              ) : (
                                <>
                                  <Trash2 className='w-3 h-3' />
                                  Delete
                                </>
                              )}
                            </button>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>

              {/* Delete Confirmation Modal */}
              {deleteConfirm && (
                <div className='fixed inset-0 bg-black/50 flex items-center justify-center z-50'>
                  <div className='bg-white rounded-lg p-6 max-w-md w-full mx-4 shadow-xl'>
                    <h3 className='text-lg font-semibold text-gray-900 mb-2'>
                      Delete Company
                    </h3>
                    <p className='text-sm text-gray-600 mb-6'>
                      Are you sure you want to delete <strong>{deleteConfirm.name}</strong>? This action cannot be undone.
                    </p>
                    <div className='flex gap-3 justify-end'>
                      <button
                        onClick={() => setDeleteConfirm(null)}
                        disabled={deletingId === deleteConfirm.id}
                        className='px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50'
                      >
                        Cancel
                      </button>
                      <button
                        onClick={() => handleDelete(deleteConfirm.id)}
                        disabled={deletingId === deleteConfirm.id}
                        className='px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center gap-2'
                      >
                        {deletingId === deleteConfirm.id ? (
                          <>
                            <div className='animate-spin rounded-full h-4 w-4 border-b-2 border-white'></div>
                            Deleting...
                          </>
                        ) : (
                          <>
                            <Trash2 className='w-4 h-4' />
                            Delete
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </AuthenticatedLayout>
  )
}
